import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { useQueryParams } from '@/hooks/useQueryParams';
import { startOf } from '@/utils/date';

import { type CageFilterUpdate, filterUpdateToParams } from './cageFilter/filterUpdate';
import {
    ALL_DAYPART_TAGS,
    DEFAULT_DAYPART_TAG,
    DEFAULT_FROM,
    DEFAULT_TO,
    DEFAULT_WEIGHT_UNIT_TAG,
    type OptoDayPartTagType,
    parseDaypartTag,
    parseFishGroupId,
    QUERY_DAYPART_TAG,
    QUERY_SELECTED_FISH_GROUP_ID,
    QUERY_WEIGHT_UNIT_TAG,
    readQueryString,
    SMOLT_DAYPART_TAG,
} from './cageFilter/params';

export type { CageFilterUpdate } from './cageFilter/filterUpdate';
export {
    CAGE_SCOPED_QUERY_PARAMS,
    cageScopedSearch,
    OptoDayPartTagType,
    QUERY_SELECTED_FISH_GROUP_ID,
    readSelectedFishGroupId,
} from './cageFilter/params';
export { useCageScopedSearch } from './cageFilter/useCageScopedSearch';

/*
 * TODO: daypartTag and weightUnitTag filtering is not
 *       fully implemented
 *
 * 1. daypartTags and weightUnitTags is not part of filter.
 *    each component must load it from somewhere else, but
 *    make sure values are correct.
 * 2. UI must be added to allow user to select daypartTag and
 *    weightUnitTag
 * 3. default daypartTag and weightUnitTag may be preconfigured
 *    and in DB (configurable by user) and loaded higher up
 *    as daypartTags and weightUnitTags (currently constants)
 */

export interface CageFilter {
    setFilter: (update: CageFilterUpdate | null) => void;
    daypartTag: OptoDayPartTagType;
    weightUnitTag: string;
    from?: Date;
    to?: Date;
    normalizedFrom?: Date;
    normalizedTo?: Date;
    /**
     * The cohort the page is scoped to, from the URL param `fg`.
     *
     * This is the only way to ask for a **closed** cohort: the cage's own context
     * can report which group is in the pen now, but it cannot guess that the
     * reader wants one that left last season. Being in the URL is what makes such
     * a view linkable and survive a refresh, and `cageScopedSearch` is what keeps
     * it across navigation between the pen's pages.
     *
     * `undefined` means "whatever occupies the pen now", and is always the
     * answer with `fish-group-tracking` off, whatever the URL says.
     */
    selectedFishGroupId?: number;
}

const CageFilterContext = createContext<CageFilter | undefined>(undefined);
CageFilterContext.displayName = 'CageFilterContext';

function useCageFilter(): CageFilter {
    const context = useContext(CageFilterContext);
    if (context == null) {
        throw new Error('useCageFilter must be used within a CageFilterProvider');
    }
    return context;
}

interface CageFilterProviderProps {
    children: ReactNode;
    from?: string;
    to?: string;
    daypartTag?: OptoDayPartTagType;
    weightUnitTag?: string;
}

function CageFilterProvider(props: CageFilterProviderProps) {
    const {
        children,
        from: propFrom,
        to: propTo,
        daypartTag: propDaypartTag,
        weightUnitTag: propWeightUnitTag,
    } = props;
    const [queryParams, setQueryParams] = useQueryParams();
    // `fg` only means anything with fish groups on. Gated here, at the one place
    // requests read it from, so a stray `fg` in the URL can never reach a payload
    // while the flag is off.
    const { enabled: fishGroupsEnabled } = usePostHogFeatureFlag('fish-group-tracking');

    const context = useMemo<CageFilter>(() => {
        const queryFrom = readQueryString(queryParams?.from);
        const queryTo = readQueryString(queryParams?.to);

        const from = startOf(queryFrom ?? propFrom, 'day') ?? DEFAULT_FROM;
        const to = startOf(queryTo ?? propTo, 'day') ?? DEFAULT_TO;

        const daypartTag =
            parseDaypartTag(queryParams?.[QUERY_DAYPART_TAG]) ??
            propDaypartTag ??
            DEFAULT_DAYPART_TAG;
        const weightUnitTag =
            readQueryString(queryParams?.[QUERY_WEIGHT_UNIT_TAG]) ??
            propWeightUnitTag ??
            DEFAULT_WEIGHT_UNIT_TAG;

        const setFilter = (update: CageFilterUpdate | null) => {
            if (update == null) {
                setQueryParams(null);
                return;
            }

            setQueryParams(filterUpdateToParams(update));
        };

        return {
            setFilter,
            daypartTag,
            weightUnitTag,
            from,
            to,
            // Cloned so callers can't mutate the shared filter state via Date setters.
            normalizedFrom: from ? new Date(from) : from,
            normalizedTo: to ? new Date(to) : to,
            selectedFishGroupId: fishGroupsEnabled
                ? parseFishGroupId(queryParams?.[QUERY_SELECTED_FISH_GROUP_ID])
                : undefined,
        };
    }, [
        queryParams,
        propFrom,
        propTo,
        propDaypartTag,
        propWeightUnitTag,
        setQueryParams,
        fishGroupsEnabled,
    ]);

    return <CageFilterContext value={context}>{children}</CageFilterContext>;
}

export default useCageFilter;
export {
    ALL_DAYPART_TAGS,
    CageFilterProvider,
    DEFAULT_DAYPART_TAG,
    DEFAULT_FROM,
    DEFAULT_TO,
    DEFAULT_WEIGHT_UNIT_TAG,
    SMOLT_DAYPART_TAG,
    useCageFilter,
};
